<script lang="ts">
    import { onDestroy } from 'svelte';
    import { push } from 'svelte-spa-router';
    import { systemStatus } from '../stores/status';
    import StatusDot from '../components/StatusDot.svelte';
    import TemperatureChart from '../components/TemperatureChart.svelte';
    import Button from '../components/forms/Button.svelte';
    import Icon from '../components/Icon.svelte';

    type LogLevel = 'error' | 'warning' | 'info' | 'success';

    function ago(seconds: number): Date {
        return new Date(Date.now() - seconds * 1000);
    }

    const logEvents: { level: LogLevel; message: string; detail: string; ts: Date }[] = [
        { level: 'success', message: 'Fire alarm cleared',   detail: 'Sector B — all sensors back within range',         ts: ago(12)      },
        { level: 'error',   message: 'Fire detected',        detail: 'Sector B — smoke + heat threshold exceeded',       ts: ago(5 * 60)  },
        { level: 'warning', message: 'Temperature high',     detail: 'Sector C — 78 °C, threshold is 75 °C',             ts: ago(14 * 60) },
        { level: 'warning', message: 'Missing sensor',       detail: 'Sensor ID 0x2F not responding',                    ts: ago(31 * 60) },
        { level: 'info',    message: 'User login',           detail: 'Admin logged in from 192.168.1.42',                ts: ago(47 * 60) },
        { level: 'info',    message: 'System update',        detail: 'Firmware 2.4.1 installed successfully',            ts: ago(1 * 3600)  },
        { level: 'success', message: 'Sensor reconnected',   detail: 'Sensor ID 0x1A back online after 3 min',          ts: ago(2 * 3600)  },
        { level: 'warning', message: 'Low battery',          detail: 'Sensor ID 0x1A — battery at 12 %',                ts: ago(2 * 3600 + 600) },
        { level: 'info',    message: 'Config saved',         detail: 'Zone thresholds updated by admin',                 ts: ago(3 * 3600)  },
        { level: 'info',    message: 'System reboot',        detail: 'Scheduled maintenance reboot completed',           ts: ago(3 * 3600 + 900) },
    ];

    function timeAgo(ts: Date, _tick: number): string {
        const s = Math.floor((Date.now() - ts.getTime()) / 1000);
        if (s < 60)  return `${s}s ago`;
        const m = Math.floor(s / 60);
        if (m < 60)  return `${m} min ago`;
        const h = Math.floor(m / 60);
        if (h < 24)  return `${h} h ago`;
        return `${Math.floor(h / 24)} d ago`;
    }

    let tick = 0;
    const timer = setInterval(() => tick++, 1000);
    onDestroy(() => clearInterval(timer));
</script>

<div class="page">
    <h1>Dashboard</h1>

    {#if $systemStatus === 'error'}
        <div class="alert alert--error">
            <StatusDot status="error" large />
            <div class="alert-body">
                <strong class="alert-title">System Error</strong>
                <p class="alert-message">The system is unreachable or has reported a critical error. Check your connection and device status.</p>
            </div>
        </div>
    {/if}

    {#if $systemStatus === 'warning'}
        <div class="alert alert--warning">
            <StatusDot status="warning" large />
            <div class="alert-body">
                <strong class="alert-title">System Warning</strong>
                <p class="alert-message">The system is reachable but has reported one or more issues. Review the device logs for details.</p>
            </div>
        </div>
    {/if}

    <section class="card">
        <h2>Temperatures</h2>
        <div class="card-body">
            <TemperatureChart />
        </div>
    </section>

    <section class="card">
        <h2>Recent events</h2>
        <ul class="log-list">
            {#each logEvents as ev}
                <li class="log-entry log-entry--{ev.level}">
                    <span class="log-level-badge">
                        <Icon name={ev.level} size={14} />
                        {ev.level}
                    </span>
                    <span class="log-body">
                        <span class="log-message">{ev.message}</span>
                        <span class="log-detail">{ev.detail}</span>
                    </span>
                    <span class="log-ago">{timeAgo(ev.ts, tick)}</span>
                </li>
            {/each}
        </ul>
        <div class="log-footer">
            <Button variant="default" on:click={() => push('/logs')}>View all logs →</Button>
        </div>
    </section>

</div>

<style lang="scss">
    .alert {
        display: flex;
        align-items: center;
        gap: 1em;
        padding: 1em 1.25em;
        margin-bottom: 1.25em;
        border: 1px solid;
        border-radius: 8px;

        &--error {
            border-color: $color-status-error;
            background-color: rgba($color-status-error, 0.08);

            .alert-title { color: $color-status-error; }
        }

        &--warning {
            border-color: $color-status-warning;
            background-color: rgba($color-status-warning, 0.08);

            .alert-title { color: $color-status-warning; }
        }
    }

    .alert-body {
        display: flex;
        flex-direction: column;
        gap: 0.25em;
    }

    .alert-title {
        font-size: 0.95em;
        font-weight: 600;
    }

    .alert-message {
        font-size: 0.875em;
        color: $color-disabled-text;
        margin: 0;
        line-height: 1.5;
    }

    // ── Log list ──────────────────────────────────────────────────────────────

    .log-list {
        list-style: none;
        margin: 0;
        padding: 0.5em 0;
    }

    .log-entry {
        display: flex;
        align-items: center;
        gap: 1.25em;
        padding: 0.875em 1.5em;
        transition: background-color 0.2s ease;
        position: relative;

        &:hover { 
            background-color: $color-bg-offset; 
        }

        &--error   .log-level-badge { background-color: rgba(#F44336, 0.12); color: #F44336; }
        &--warning .log-level-badge { background-color: rgba(#FFB300, 0.12); color: #FFB300; }
        &--success .log-level-badge { background-color: rgba(#4CAF50, 0.12); color: #4CAF50; }
        &--info    .log-level-badge { background-color: $color-bg-active; color: $color-text-secondary; }

        &::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 1.5em;
            right: 1.5em;
            height: 1px;
            background-color: $color-border;
            opacity: 0.5;
        }

        &:last-child::after {
            display: none;
        }
    }

    .log-level-badge {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5em;
        padding: 0.25em 0.75em;
        border-radius: 100px;
        font-size: 0.65em;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        flex-shrink: 0;
        min-width: 80px;
    }

    .log-body {
        display: flex;
        flex-direction: column;
        gap: 0.15em;
        flex: 1;
        min-width: 0;
    }

    .log-message {
        font-size: 0.9em;
        font-weight: 500;
        color: $color-text;
    }

    .log-detail {
        font-size: 0.82em;
        color: $color-text-secondary;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .log-ago {
        font-size: 0.78em;
        color: $color-disabled-text;
        white-space: nowrap;
        flex-shrink: 0;
        font-feature-settings: "tnum";
    }

    .log-footer {
        padding: 1em 1.5em;
        display: flex;
        justify-content: flex-end;
    }
</style>
